import { useCallback, useEffect, useRef, useState } from "react";
import { reconcileOnMount, type BusEnvelope } from "@perlab/cross-tab";
import { readCookie, writeCookie } from "../utils/cookie";
import { useBridge, useBus, useKv } from "./_cross-tab-context";

export type Theme = "light" | "dark" | "system";
export type ResolvedTheme = "light" | "dark";

const COOKIE = "shell.theme";
const THEME_CHANGED = "theme.changed";

interface ThemeChangedPayload {
  theme: Theme;
}

function systemPrefersDark(): boolean {
  if (typeof window === "undefined") return false;
  return window.matchMedia?.("(prefers-color-scheme: dark)").matches ?? false;
}

function readInitial(): Theme {
  const stored = readCookie(COOKIE);
  if (stored === "light" || stored === "dark" || stored === "system") return stored;
  return "dark"; // dark-first per the canonical PERL DS; a saved cookie still overrides (light = opt-out)
}

function resolve(t: Theme): ResolvedTheme {
  if (t === "light" || t === "dark") return t;
  return systemPrefersDark() ? "dark" : "light";
}

function applyTheme(resolved: ResolvedTheme) {
  if (typeof document === "undefined") return;
  document.documentElement.setAttribute("data-theme", resolved);
}

/**
 * Tier 1 V3 Slice 1 tasks 1.17 + 1.20 — cross-tab theme sync with focus guard.
 *
 * Wiring (per spec §3.2.4 echo guard + §4.6 focus guard):
 *   - Local change (setTheme/toggle): updates state + publishes 'theme.changed'.
 *     The publish runs synchronously inside the setter — no risk of the
 *     suppressBroadcastForType microtask clearing before the effect re-renders.
 *   - Remote arrival: bus.suppressBroadcastForType('theme.changed') THEN
 *     setThemeState — the existing [theme] effect applies + writes cookie,
 *     but does NOT re-publish (publish lives in setTheme/toggle only,
 *     not in the apply effect). Defense-in-depth: the suppress flag guards
 *     against any future code path that might publish from the effect.
 *   - Focus guard: when this tab is focused AND has a HostBridge in scope
 *     with unsaved-state declared, defer the remote apply. On window blur,
 *     apply the latest deferred snapshot (one per type — last-deferred slot
 *     semantics resolve MED N13). When no bridge is in scope, fail closed
 *     (defer when focused) per §4.6 MED N17.
 *   - Reconcile-on-mount: when the kv adapter is available, fetch the last
 *     'theme.changed' snapshot and apply it (also through the focus guard)
 *     so a late-joining tab picks up the current cross-tab theme.
 *
 * Slice 2 deferred per TL ruling (drain 3):
 *   - persistSnapshot post-publish: requires bus.publish to return the
 *     constructed envelope. Without env access from the public API, this
 *     hook cannot write the replay row. Spec §3.2.5 places the write at
 *     the publish boundary — Slice 2 will likely extend bus.ts to handle
 *     this internally OR add a publish-returns-envelope variant.
 */
export function useTheme() {
  const [theme, setThemeState] = useState<Theme>(readInitial);
  const [resolved, setResolved] = useState<ResolvedTheme>(() => resolve(readInitial()));

  // Cross-tab plumbing — all may be null when no BusProvider is in the tree
  // (e.g. storybook, tests). Null = single-tab fallback; the hook still works.
  const bus = useBus();
  const bridge = useBridge();
  const kv = useKv();

  // Per-type deferred-slot Map. Resolves MED N13: only one envelope per type
  // is retained — repeated remote changes during focused-and-unsaved windows
  // overwrite latest. On blur, this drains to one apply per type.
  //
  // useRef (not useState) because deferral updates MUST NOT trigger re-render
  // — they're queued for blur replay, not displayed state.
  const deferredByType = useRef<Map<string, BusEnvelope<ThemeChangedPayload>>>(
    new Map(),
  );

  // Apply effect — fires on any theme state change (local OR remote). Writes
  // the resolved DOM attribute + persists the user preference cookie.
  // Idempotent: same value re-applied is a no-op.
  useEffect(() => {
    const r = resolve(theme);
    setResolved(r);
    applyTheme(r);
    writeCookie(COOKIE, theme);
  }, [theme]);

  // Listen for OS-level theme flips when on "system".
  useEffect(() => {
    if (theme !== "system" || typeof window === "undefined") return;
    const mql = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => {
      const r: ResolvedTheme = mql.matches ? "dark" : "light";
      setResolved(r);
      applyTheme(r);
    };
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, [theme]);

  // ----- Focus-guard primitives (spec §4.6) -----

  /**
   * Per §4.6: apply immediately when the tab is NOT focused. When focused,
   * consult the bridge's hasUnsavedState; defer when unsaved or when there
   * is no bridge in scope (fail-closed default per MED N17).
   *
   * Async because bridge.queryUnsavedState returns a Promise (fail-closed
   * to true on timeout — handled inside iframe-bridge host.ts).
   */
  const shouldApplyRemote = useCallback(async (): Promise<boolean> => {
    if (typeof document === "undefined") return true; // SSR-safe
    const focused = document.visibilityState === "visible" && document.hasFocus();
    if (!focused) return true;
    if (!bridge) return false; // No bridge in scope → defer (fail-closed)
    try {
      const hasUnsaved = await bridge.queryUnsavedState();
      return !hasUnsaved;
    } catch {
      // queryUnsavedState should never reject (fail-closes to true), but
      // belt-and-suspenders: a thrown error defers per fail-closed intent.
      return false;
    }
  }, [bridge]);

  /**
   * Apply a remote envelope through the focus guard. Echo-guard via
   * suppressBroadcastForType ensures the local setThemeState that follows
   * does NOT re-publish even if a future code path attempts to.
   */
  const applyEnvelope = useCallback(
    (env: BusEnvelope<ThemeChangedPayload>) => {
      const next = env.payload?.theme;
      if (next !== "light" && next !== "dark" && next !== "system") return;
      // Echo guard — same-microtask publish drops; release auto-clears.
      bus?.suppressBroadcastForType(THEME_CHANGED);
      setThemeState(next);
    },
    [bus],
  );

  const applyOrDefer = useCallback(
    async (env: BusEnvelope<ThemeChangedPayload>) => {
      const ok = await shouldApplyRemote();
      if (ok) {
        applyEnvelope(env);
        return;
      }
      // Defer — overwrite latest for this type (resolves MED N13).
      deferredByType.current.set(env.type, env);
    },
    [applyEnvelope, shouldApplyRemote],
  );

  // ----- Bus subscribe: theme.changed -----

  useEffect(() => {
    if (!bus) return;
    const off = bus.subscribe<ThemeChangedPayload>(THEME_CHANGED, (env) => {
      // Fire and forget — applyOrDefer handles its own async path. We don't
      // await because bus handlers are synchronous-only by contract.
      void applyOrDefer(env);
    });
    return () => off();
  }, [bus, applyOrDefer]);

  // ----- Window blur: drain deferred -----

  useEffect(() => {
    if (typeof window === "undefined") return;
    const onBlur = () => {
      const drained = Array.from(deferredByType.current.values());
      deferredByType.current.clear();
      for (const env of drained) {
        applyEnvelope(env);
      }
    };
    window.addEventListener("blur", onBlur);
    return () => window.removeEventListener("blur", onBlur);
  }, [applyEnvelope]);

  // ----- Reconcile-on-mount: pull last theme.changed snapshot from kv -----

  useEffect(() => {
    if (!bus || !kv) return;
    let cancelled = false;
    void reconcileOnMount<ThemeChangedPayload>(
      kv,
      THEME_CHANGED,
      bus.tabId,
      (env) => {
        if (cancelled) return;
        // Route through the focus guard — a tab opening focused-with-
        // unsaved-state still defers replay until blur (consistent UX).
        void applyOrDefer(env);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [bus, kv, applyOrDefer]);

  // ----- Local setters: publish on change -----

  const setTheme = useCallback(
    (t: Theme) => {
      setThemeState(t);
      // Publish runs synchronously here — no microtask gap means the bus's
      // suppressBroadcastForType (set by applyEnvelope on inbound) is still
      // active when this fires during the apply-remote path. Local-only
      // calls (user toggles via UI) reach this with no active suppress.
      bus?.publish<ThemeChangedPayload>(THEME_CHANGED, { theme: t });
    },
    [bus],
  );

  const toggle = useCallback(() => {
    setThemeState((current) => {
      const next: Theme = resolve(current) === "light" ? "dark" : "light";
      // Publish the resolved binary flip — receivers see a concrete theme,
      // not a "system" indirection.
      bus?.publish<ThemeChangedPayload>(THEME_CHANGED, { theme: next });
      return next;
    });
  }, [bus]);

  return { theme, resolvedTheme: resolved, setTheme, toggle };
}
